import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, getAdminApiUrl } from '../lib/supabase';
import { validatePassword } from '../lib/passwordValidation';
import Logo from './Logo';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle Supabase password reset flow
    const handlePasswordReset = async () => {
      try {
        // Check if we have tokens from Supabase verification
        // Supabase may provide tokens either as query params or in the URL hash fragment
        const queryAccessToken = searchParams.get('access_token');
        const queryRefreshToken = searchParams.get('refresh_token');

        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const hashAccessToken = hashParams.get('access_token');
        const hashRefreshToken = hashParams.get('refresh_token');

        const token = searchParams.get('token');
        const type = searchParams.get('type');

        // Case 1: We have access/refresh tokens (from query or hash) → set the session
        const accessToken = queryAccessToken || hashAccessToken;
        const refreshToken = queryRefreshToken || hashRefreshToken;
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
          if (error) {
            setError('Invalid or expired reset link. Please request a new password reset.');
            return;
          }
          // Session is now set; user can proceed to update password
          // Clear any previous errors
          setError('');
          return;
        } else if (token && type === 'recovery') {
          // Supabase verification token - we need to exchange it for session tokens
          const { error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'recovery'
          });

          if (error) {
            setError('Invalid or expired reset link. Please request a new password reset.');
            return;
          }

          // If verification successful, the user session is now set
          // We can proceed with password reset
          // Clear any previous errors
          setError('');
        }
        // If no tokens or recovery type, just let the user try
        // They might have a valid session already
      } catch (error) {
        // Only set error for actual exceptions, not missing tokens
        console.error('Password reset initialization error:', error);
      }
    };

    handlePasswordReset();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;
    
    // Validation
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors.join(', '));
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Check if we have a valid session (either from direct tokens or verification)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Invalid or expired reset link. Please request a new password reset.');
      }

      // Try to update the password directly
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        // Check if it's an MFA-related error
        if (updateError.message.includes('AAL2 session') || updateError.message.includes('MFA')) {
          // For MFA-enabled users, try to use admin API to bypass MFA
          // This will work for admin users but fail for non-admin users
          const userId = session.user.id;
          
          try {
            // Try to use admin API to update password (bypasses MFA requirement)
            const response = await fetch(getAdminApiUrl(), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                action: 'updateUserPassword',
                userId: userId,
                data: { password: password }
              })
            });

            if (response.ok) {
              // Success! User was an admin and password was updated via admin API
              // Continue with success flow (don't throw error)
            } else if (response.status === 403) {
              // User is not admin, they can't bypass MFA
              throw new Error('This user has Multi-Factor Authentication (MFA) enabled. Password reset requires MFA verification. Please contact an administrator for assistance.');
            } else {
              // Other error
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to update password');
            }
          } catch (adminError: any) {
            // If admin API call fails for any reason, assume user is not admin
            throw new Error('This user has Multi-Factor Authentication (MFA) enabled. Password reset requires MFA verification. Please contact an administrator for assistance.');
          }
        } else {
          throw updateError;
        }
      }

      setSuccess(true);
      
      // Check if user is admin to determine redirect destination
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          // Try to check if user is admin
          const adminCheckResponse = await fetch(getAdminApiUrl(), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'verifyAdminStatus',
              userId: session.user.id
            })
          });

          if (adminCheckResponse.ok) {
            const adminResult = await adminCheckResponse.json();
            if (adminResult.isAdmin) {
              // Admin user - redirect to admin login
              setTimeout(() => {
                navigate('/');
              }, 3000);
            } else {
              // Non-admin user - redirect to mobile app instructions
              setTimeout(() => {
                navigate('/mobile-app-instructions');
              }, 3000);
            }
          } else {
            // If admin check fails, assume non-admin and redirect to mobile app instructions
            setTimeout(() => {
              navigate('/mobile-app-instructions');
            }, 3000);
          }
        } else {
          // No session, redirect to mobile app instructions as fallback
          setTimeout(() => {
            navigate('/mobile-app-instructions');
          }, 3000);
        }
      } catch (error) {
        // If any error occurs, redirect to mobile app instructions as fallback
        setTimeout(() => {
          navigate('/mobile-app-instructions');
        }, 3000);
      }

    } catch (error: any) {
      setError(error.message || 'An error occurred while resetting your password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Logo />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Password Reset Successful
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your password has been successfully updated. You will be redirected shortly.
            </p>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <Logo />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password below
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Enter new password"
              />
              <p className="mt-1 text-xs text-gray-500">
                Password must be at least 8 characters with uppercase, lowercase, number, and special character
              </p>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
